import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api, { ADMIN_BASE, assetUrl, formatApiError } from '../../lib/api';

const empty = {
  category: 'refrigerators',
  brand: '',
  model: '',
  gtin: '',
  name_en: '',
  name_es: '',
  description_en: '',
  description_es: '',
  price: 0,
  images: [],
  image: '',
  specs: {},
  in_stock: true,
};

const CATEGORY_OPTIONS = [
  { value: 'refrigerators',  label: 'Refrigerators' },
  { value: 'stoves',         label: 'Stoves & Ranges' },
  { value: 'washers-dryers', label: 'Washers & Dryers' },
  { value: 'microwaves',     label: 'Microwaves' },
];

export default function AdminProductForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();

  const [form, setForm] = useState(empty);
  const [specRows, setSpecRows] = useState([{ k: '', v: '' }]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    api.get(`/products/${id}`).then(r => {
      const p = r.data;
      setForm({ ...empty, ...p, images: p.images && p.images.length ? p.images : (p.image ? [p.image] : []) });
      const sr = Object.entries(p.specs || {}).map(([k, v]) => ({ k, v }));
      setSpecRows(sr.length ? sr : [{ k: '', v: '' }]);
    }).catch(e => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, editing]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, images: [...(f.images || []), data.url], image: f.image || data.url }));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const removeImage = (idx) => {
    setForm(f => {
      const next = [...(f.images || [])];
      next.splice(idx, 1);
      const primary = f.image && !next.includes(f.image) ? (next[0] || '') : f.image;
      return { ...f, images: next, image: primary };
    });
  };

  const setPrimary = (url) => setForm(f => ({ ...f, image: url }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const specs = {};
      specRows.forEach(({ k, v }) => { if (k.trim() && v.trim()) specs[k.trim()] = v.trim(); });
      const payload = { ...form, specs, price: Number(form.price) };
      if (editing) {
        await api.patch(`/admin/products/${id}`, payload);
      } else {
        await api.post('/admin/products', payload);
      }
      nav(ADMIN_BASE);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="admin-loading"><i className="fa-solid fa-spinner fa-spin"></i><span>Loading…</span></div>;
  }

  return (
    <div>
      <div className="admin-page-head">
        <div>
          <Link to={ADMIN_BASE} className="back-link" data-testid="admin-back-link">
            <i className="fa-solid fa-arrow-left"></i><span>All products</span>
          </Link>
          <h1>{editing ? 'Edit product' : 'New product'}</h1>
        </div>
      </div>

      <form onSubmit={onSubmit} className="admin-form-grid">
        <section className="admin-card">
          <h2 className="admin-card-h">Basics</h2>

          <div className="grid-2">
            <label>
              <span>Category</span>
              <select value={form.category} onChange={e => update('category', e.target.value)} required data-testid="form-category">
                {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label>
              <span>Brand</span>
              <input type="text" value={form.brand} onChange={e => update('brand', e.target.value)} required placeholder="Samsung, LG, Whirlpool…" data-testid="form-brand" />
            </label>
          </div>

          <label>
            <span>Model number</span>
            <input type="text" value={form.model} onChange={e => update('model', e.target.value)} required placeholder="RF28T5021SR" data-testid="form-model" />
          </label>

          <label>
            <span>GTIN / UPC <span style={{ color: 'var(--muted)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>— optional, helps Google Shopping match your listings</span></span>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={form.gtin} onChange={e => update('gtin', e.target.value)} placeholder="887276450018" data-testid="form-gtin" />
          </label>

          <div className="grid-2">
            <label>
              <span>Price (USD)</span>
              <input type="number" step="0.01" min="0" value={form.price} onChange={e => update('price', e.target.value)} required data-testid="form-price" />
            </label>
            <label className="check">
              <input type="checkbox" checked={form.in_stock} onChange={e => update('in_stock', e.target.checked)} data-testid="form-instock" />
              <span>In stock</span>
            </label>
          </div>
        </section>

        <section className="admin-card">
          <h2 className="admin-card-h">Names &amp; descriptions</h2>
          <div className="grid-2">
            <label>
              <span>Name (English)</span>
              <input type="text" value={form.name_en} onChange={e => update('name_en', e.target.value)} required data-testid="form-name-en" />
            </label>
            <label>
              <span>Nombre (Español)</span>
              <input type="text" value={form.name_es} onChange={e => update('name_es', e.target.value)} required data-testid="form-name-es" />
            </label>
          </div>
          <div className="grid-2">
            <label>
              <span>Description (English)</span>
              <textarea rows="4" value={form.description_en} onChange={e => update('description_en', e.target.value)} required data-testid="form-desc-en" />
            </label>
            <label>
              <span>Descripción (Español)</span>
              <textarea rows="4" value={form.description_es} onChange={e => update('description_es', e.target.value)} required data-testid="form-desc-es" />
            </label>
          </div>
        </section>

        <section className="admin-card">
          <h2 className="admin-card-h">Photos</h2>
          <p className="muted" style={{ marginTop: -6, marginBottom: 14 }}>Upload one or more photos. Click a thumbnail to set it as the primary.</p>

          <div className="image-grid">
            {(form.images || []).map((url, idx) => (
              <div key={url + idx} className={`image-tile ${form.image === url ? 'primary' : ''}`} data-testid={`image-tile-${idx}`}>
                <img src={assetUrl(url)} alt={`product-${idx}`} onError={(e) => { e.currentTarget.style.opacity = .3; }} />
                <div className="image-tile-actions">
                  <button type="button" onClick={() => setPrimary(url)} title="Set primary" data-testid={`set-primary-${idx}`}>
                    <i className="fa-solid fa-star"></i>
                  </button>
                  <button type="button" className="danger" onClick={() => removeImage(idx)} title="Remove" data-testid={`remove-image-${idx}`}>
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
                {form.image === url && <span className="primary-pill">Primary</span>}
              </div>
            ))}

            <label className="image-upload">
              <input type="file" accept="image/*" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value=''; }} hidden data-testid="form-upload-input" />
              <i className={uploading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-cloud-arrow-up'}></i>
              <span>{uploading ? 'Uploading…' : 'Upload image'}</span>
            </label>
          </div>
        </section>

        <section className="admin-card">
          <h2 className="admin-card-h">Specifications</h2>
          {specRows.map((row, idx) => (
            <div key={idx} className="spec-input-row">
              <input type="text" placeholder="Capacity" value={row.k} onChange={e => setSpecRows(rs => rs.map((r, i) => i === idx ? { ...r, k: e.target.value } : r))} data-testid={`spec-key-${idx}`} />
              <input type="text" placeholder="28 cu ft" value={row.v} onChange={e => setSpecRows(rs => rs.map((r, i) => i === idx ? { ...r, v: e.target.value } : r))} data-testid={`spec-value-${idx}`} />
              <button type="button" className="icon-btn danger" onClick={() => setSpecRows(rs => rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs)} title="Remove">
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-ghost" onClick={() => setSpecRows(rs => [...rs, { k: '', v: '' }])} data-testid="add-spec-btn">
            <i className="fa-solid fa-plus"></i><span>Add specification</span>
          </button>
        </section>

        {err && <div className="form-error">{err}</div>}

        <div className="admin-form-actions">
          <Link to={ADMIN_BASE} className="btn btn-ghost">Cancel</Link>
          <button type="submit" className="btn btn-primary btn-lg" disabled={saving} data-testid="form-save-btn">
            <i className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'}></i>
            <span>{saving ? 'Saving…' : (editing ? 'Save changes' : 'Create product')}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
